import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as loginApi, logout as logoutApi, getMe, switchTenant as switchTenantApi } from '../services/authService';
import history from '../routes/history';
import { resolveHasSeat } from './resolveHasSeat';
import IdleSessionGuard from '../components/session/IdleSessionGuard';
import {
  broadcastSessionSignal,
  clearSessionActivity,
  markActivityNow,
  subscribeSessionSignal,
} from '../services/sessionActivity';

const REMEMBER_KEY = 'flexbee_remember';

// "E-postamı hatırla": yalnız giriş e-postası saklanır. Web oturumu artık hareketsizlik zaman
// aşımına tabidir; uzun ömürlü ("30 gün") web oturumu istenmez — backend'e rememberMe
// gönderilmez, oturum varsayılan süreyle (Jwt:ExpireMinutes) yaşar.
const rememberEmail = (email, remember) => {
  try {
    if (remember) localStorage.setItem(REMEMBER_KEY, JSON.stringify({ email, rememberMe: true }));
    else localStorage.removeItem(REMEMBER_KEY);
  } catch { /* depolama yok */ }
};

// Hareketsizlikten çıkışta kullanıcı giriş sonrası kaldığı sayfaya döner.
const idleLoginPath = () => {
  const here = `${window.location.pathname}${window.location.search}`;
  const redirect = here.startsWith('/login') ? '' : `&redirect=${encodeURIComponent(here)}`;
  return `/login?reason=idle${redirect}`;
};

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Initial auth check
  useEffect(() => {
    getMe()
      .then((res) => {
        if (res.data?.data) {
          setUser(res.data.data);
        } else {
          setUser(null);
        }
      })
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  // Listen for forced logout (401 from interceptor)
  useEffect(() => {
    const handler = () => {
      clearSessionActivity();
      setUser(null);
      if (window.location.pathname !== '/login') {
        history.push('/login');
      }
    };
    window.addEventListener('auth:logout', handler);
    return () => window.removeEventListener('auth:logout', handler);
  }, []);

  // Başka bir sekmede çıkış yapıldı: bu sekme de oturumsuz kalmalı (çerez ortaktır, ilk istekte
  // 401 alacaktı) — beklemeden giriş ekranına geç.
  useEffect(() => subscribeSessionSignal((type) => {
    if (type !== 'logout') return;
    setUser(null);
    if (window.location.pathname !== '/login') history.push('/login');
  }), []);

  // Listen for a silent session refresh (403 self-heal in the API interceptor):
  // adopt the freshly recomputed privileges/modules so gated menus and routes
  // update live, without a page reload.
  useEffect(() => {
    const handler = (e) => { if (e.detail) setUser(e.detail); };
    window.addEventListener('auth:refreshed', handler);
    return () => window.removeEventListener('auth:refreshed', handler);
  }, []);

  const isSuperAdmin = user?.privileges?.includes('superAdmin');
  const isEnterpriseAdmin = user?.privileges?.includes('enterpriseAdmin');
  // İK rolü ayrımı (2026-10-28) — oturum kiracının İK'sı mı. Karar SUNUCUDADIR
  // (`user.isHr` — HrActorRules: "İK Rolü" işaretli rolün sahibi; kiracı İK rolü
  // kullanmıyorsa Şirket Yöneticisi). İK kuyrukları, İK raporları ve org geneli PM
  // yüzeyleri bu bayrağa bağlıdır; `isEnterpriseAdmin` artık "İK" anlamına GELMEZ.
  // Alanı HİÇ taşımayan eski oturumda eski kural (İK = enterpriseAdmin) uygulanır.
  const isHr = Boolean(isSuperAdmin)
    || (typeof user?.isHr === 'boolean' ? user.isHr : Boolean(isEnterpriseAdmin));

  // İşe hazırlık kilidi: aktif preboarding'i olan normal kullanıcı yalnızca
  // İşe Hazırlık (+ profil) görür. Admin'ler asla kilitlenmez.
  const isPreboardingLocked =
    Boolean(user?.hasActivePreboarding) && !isSuperAdmin && !isEnterpriseAdmin;

  const hasPermission = useCallback(
    (module, type) => {
      if (isSuperAdmin) return true;
      if (isEnterpriseAdmin) return true;
      return user?.privileges?.includes(`${module}.${type}`);
    },
    [user, isSuperAdmin, isEnterpriseAdmin]
  );

  const canRead = useCallback((module) => hasPermission(module, 'Read'), [hasPermission]);
  const canWrite = useCallback((module) => hasPermission(module, 'Write'), [hasPermission]);

  // Module AVAILABILITY in the active tenant (subscription), independent of the
  // user's own privileges. Sessions issued before the modules field existed
  // lack it — fall back to privileges so gated menus don't vanish until re-login.
  const hasModule = useCallback(
    (code) => {
      if (isSuperAdmin) return true;
      if (!user?.modules) return Boolean(user?.privileges?.some((p) => p.startsWith(`${code}.`)));
      return user.modules.includes(code);
    },
    [user, isSuperAdmin]
  );

  // Koltuk (lisans) kontrolü. SEATS'İN ANLAMI (İ-4 — eski yorum bunu TERSİNE
  // anlatıyordu): ölçümsüz ve varsayılan modüller listede YER ALIR; listeden
  // yalnız KOLTUKSUZ ÖLÇÜMLÜ modüller düşer (AuthorizeDataAccess.ResolveSeatCodes:
  // `activeModules.Where(code => !olcumlu.Contains(code) || koltuklu.Contains(code))`).
  // Bu tanım DEĞİŞMEZ 0'ın kendisidir: liste yalnız koltuklu kodları taşısaydı
  // ölçümsüz her `moduleGated` menü (LM, TA, …) hiçbir kiracıda kimseye görünmezdi.
  // Ölçümlü havuzu olmayan bağlamda `seats == modules` olur. `seats` alanı hiç
  // olmayan eski oturumlarda true döner (sürüm geçişi kilitlenmesin).
  const hasSeat = useCallback(
    (code) => resolveHasSeat({ user, isSuperAdmin }, code),
    [user, isSuperAdmin]
  );

  // Oturum yanıtını (giriş ya da MFA sınamasının tamamlanması) benimser. MFA adımında
  // sunucu çerezi zaten yazmıştır; kullanıcı ancak kurtarma kodlarını kaydettiğini onayladıktan
  // sonra uygulamaya alınır — bu yüzden benimseme çağıranın kontrolündedir.
  const adoptSession = useCallback((sessionUser, credentials) => {
    if (!sessionUser) return;
    markActivityNow();
    rememberEmail(credentials?.email, Boolean(credentials?.rememberMe));
    setUser(sessionUser);
  }, []);

  const login = async (credentials) => {
    const res = await loginApi({ email: credentials.email, password: credentials.password, rememberMe: false });
    if (res.data?.data) adoptSession(res.data.data, credentials);
    return res;
  };

  const logout = async () => {
    try {
      await logoutApi();
    } catch {
      // Ignore logout errors
    }
    clearSessionActivity();
    broadcastSessionSignal('logout');
    setUser(null);
    history.push('/login');
  };

  // Hareketsizlik zaman aşımı. broadcast=false: çıkışı başka bir sekme başlattı — sunucu
  // oturumunu o kapattı, burada yalnız arayüz temizlenir.
  const logoutForIdle = useCallback(async ({ broadcast = true } = {}) => {
    const target = idleLoginPath();
    if (broadcast) {
      try {
        await logoutApi({ skipAuthRedirect: true, skipErrorToast: true });
      } catch {
        // Sunucu penceresi zaten kapanmış olabilir (401) — arayüz yine de çıkış yapar.
      }
      broadcastSessionSignal('idle-logout');
    }
    clearSessionActivity();
    setUser(null);
    history.push(target);
  }, []);

  const switchTenant = async (tenantId) => {
    const res = await switchTenantApi(tenantId);
    if (res.data?.data) {
      // Full page reload instead of history.push('/'): when the user is
      // already ON the dashboard, a same-path push does not remount it, so
      // every card kept showing the previous tenant's data. The session
      // cookie was already rewritten by the backend — a hard reload boots
      // the whole app (and every mount-time fetch) in the new tenant context.
      window.location.assign('/');
    }
    return res;
  };

  const accessibleTenants = user?.accessibleTenants || [];
  const homeTenant = user?.homeTenant || null;
  // Switching scope = home + admin-scope tenants. The home tenant is always
  // reachable so an employee assigned admin to a sibling subsidiary can still
  // navigate back to their home context.
  const switchableTenants = (() => {
    const list = [...accessibleTenants];
    if (homeTenant && !list.find((t) => t.id === homeTenant.id)) list.unshift(homeTenant);
    return list;
  })();
  const canSwitchTenant = !isSuperAdmin && switchableTenants.length > 1;
  // canManageTenants stays gated on admin scope (accessibleTenants), NOT
  // switchableTenants — being able to navigate to the home tenant doesn't
  // imply being able to manage it.
  const activeTenant = accessibleTenants.find((t) => t.id === user?.tenantId);
  const isHoldingActiveTenant = activeTenant?.tenantType === 1;
  const canManageTenants = isSuperAdmin || accessibleTenants.length > 1 || isHoldingActiveTenant;

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isSuperAdmin,
        isEnterpriseAdmin,
        isHr,
        isPreboardingLocked,
        hasPermission,
        canRead,
        canWrite,
        hasModule,
        hasSeat,
        login,
        adoptSession,
        logout,
        switchTenant,
        accessibleTenants,
        switchableTenants,
        canSwitchTenant,
        canManageTenants,
        activeTenantId: user?.tenantId,
        homeTenantId: user?.homeTenantId,
        homeTenant,
      }}
    >
      {children}
      {user && (
        <IdleSessionGuard
          timeoutMinutes={user.sessionIdleTimeoutMinutes}
          onIdleLogout={logoutForIdle}
          onLogout={logout}
        />
      )}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
